/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { FeatureIdWithMID, MerchantIdType } from 'lib/typing';
import apiEndpoints from '../lib/apiEndpoints';
import apiCall from './apiCall';

const stripCancelKey = <T extends Record<string, any>>(payload: T = {} as T) => {
  const { cancelKey, ...requestPayload } = payload;
  return { cancelKey, requestPayload };
};

export const getActiveWebhookEventsApi = (payload) => {
  const { merchantId } = payload;
  return apiCall({
    method: 'get',
    endpoint: `${apiEndpoints.GET_ACTIVE_WEBHOOK_EVENTS}/${merchantId}`,
  });
};

export const getMerchentSettingsApi = (payload) => {
  const { merchantId } = payload;
  return apiCall({
    method: 'get',
    endpoint: `${apiEndpoints.GET_MERCHENT_SETTINGS}/${merchantId}`,
  });
};

export const updateMerchentGeneralSettingsApi = (payload) => {
  return apiCall({
    method: 'post',
    payload,
    endpoint: apiEndpoints.UPDATE_MERCHENT_GENERAL_SETTINGS,
  });
};

export const updateMerchentWebhookApi = (payload) => {
  return apiCall({
    method: 'post',
    payload,
    endpoint: apiEndpoints.UPDATE_MERCHENT_WEBHOOK,
  });
};

/**
 * User Management APIs
 */
export const getActiveUsersApi = (data: {
  merchantId: string | number;
  pageNo?: string | number;
  limit?: string | number;
  sortBy?: string | null;
  orderBy?: string | null;
  isAdmin?: boolean;
  cancelKey?: string;
}) => {
  const { cancelKey, requestPayload } = stripCancelKey(data);
  const { merchantId, pageNo, limit, sortBy, orderBy, isAdmin } = requestPayload;
  let endpoint = `${apiEndpoints.UNIFIED_GET_ACTIVE_USERS}/${merchantId}`;
  isAdmin && merchantId === 0
    ? (endpoint = `${apiEndpoints.ADMIN_GET_ACTIVE_USERS}`)
    : '';
  return apiCall({
    method: 'get',
    endpoint: endpoint,
    query: {
      pageNo,
      limit,
      sortBy,
      orderBy,
    },
    cancelKey,
  });
};

export const getAllRolesApi = (data: {
  merchantId: number | string;
  isAdmin?: boolean;
  cancelKey?: string;
}) => {
  const { cancelKey, requestPayload } = stripCancelKey(data);
  const { merchantId, isAdmin } = requestPayload;

  let endpoint = `${apiEndpoints.UNIFIED_GET_ALL_ROLES}/${merchantId}`;
  isAdmin && merchantId === 0
    ? (endpoint = `${apiEndpoints.ADMIN_GET_ALL_ROLES}`)
    : '';

  return apiCall({
    method: 'get',
    endpoint: endpoint,
    cancelKey,
  });
};

export const getAllPermissionsApi = (data: {
  merchantId: number | string;
  isAdmin?: boolean;
  cancelKey?: string;
}) => {
  const { cancelKey, requestPayload } = stripCancelKey(data);
  const { merchantId, isAdmin } = requestPayload;

  let endpoint = apiEndpoints.GET_ALL_PERMISSIONS_LIST;
  isAdmin && merchantId === 0
    ? (endpoint = `${apiEndpoints.ADMIN_GET_ALL_PERMISSIONS_LIST}`)
    : '';
  return apiCall({
    method: 'get',
    endpoint: endpoint,
    cancelKey,
  });
};

export const createNewRoleApi = (payload) => {
  let endpoint = apiEndpoints.UNIFIED_CREATE_NEW_ROLE;
  payload.isAdmin ? (endpoint = `${apiEndpoints.ADMIN_CREATE_NEW_ROLE}`) : '';
  return apiCall({
    method: 'post',
    payload,
    endpoint: endpoint,
  });
};

export const createNewUserApi = (payload) => {
  let endpoint = apiEndpoints.UNIFIED_CREATE_NEW_USER;
  payload.isAdmin && payload.merchant_id === 0
    ? (endpoint = `${apiEndpoints.ADMIN_CREATE_NEW_USER}`)
    : '';
  return apiCall({
    method: 'post',
    payload,
    endpoint: endpoint,
  });
};

export const updateUserDetailsApi = (payload) => {
  let endpoint = apiEndpoints.UNIFIED_UPDATE_USER_DETAILS;
  payload.isAdmin && payload.merchantId === 0
    ? (endpoint = `${apiEndpoints.ADMIN_UPDATE_USER_DETAILS}`)
    : '';

    if(payload?.isShowGuidedTourUpdated) {
      delete payload?.isShowGuidedTourUpdated;
    }

  return apiCall({
    method: 'post',
    payload,
    endpoint: endpoint,
  });
};

export const deleteUserApi = (payload) => {
  const { user_id, isAdmin, merchantId } = payload;
  let endpoint = apiEndpoints.UNIFIED_DELETE_USER;
  isAdmin && merchantId === 0
    ? (endpoint = `${apiEndpoints.ADMIN_DELETE_USER}`)
    : '';
  return apiCall({
    method: 'delete',
    payload,
    endpoint: `${endpoint}/${user_id}`,
  });
};

export const resendInvitationApi = (payload: {
  isAdmin: boolean;
  merchantId: number;
  userId: number;
}) => {
  const { isAdmin, merchantId } = payload;
  let endpoint = apiEndpoints.UNIFIED_RESEND_INVITATION;
  isAdmin && merchantId === 0
    ? (endpoint = `${apiEndpoints.ADMIN_RESEND_INVITATION}`)
    : '';
  return apiCall({
    method: 'post',
    payload,
    endpoint: `${endpoint}`,
  });
};

// Checkout Theme
export const getCheckoutThemeApi = (payload: {
  merchantId: string | number;
  cancelKey?: string;
}) => {
  const { cancelKey, requestPayload } = stripCancelKey(payload);
  const { merchantId } = requestPayload;
  return apiCall({
    method: 'get',
    endpoint: `${apiEndpoints.GET_CHECKOUT_THEME}/${merchantId}`,
    cancelKey,
  });
};

export const getEdgeCheckoutThemeApi = (payload = {}) => {
  const { cancelKey } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.GET_EDGE_CHECKOUT_THEME,
    cancelKey,
  });
};


export const updateEdgeCheckoutThemeApi = (payload: any) => {
  return apiCall({
    method: 'post',
    payload,
    endpoint: `${apiEndpoints.UPDATE_EDGE_CHECKOUT_THEME}`,
  });
};

export const updateCheckoutThemeApi = (payload: {
  theme_color: number;
  logo_url: string;
  merchant_id: number | string;
}) => {
  return apiCall({
    method: 'post',
    payload,
    endpoint: `${apiEndpoints.UPDATE_CHECKOUT_THEME}`,
  });
};

export const updateMerchantTPV = (data: {
  enable_tpv: boolean;
  merchant_id: MerchantIdType;
}): void => {
  return apiCall({
    method: 'put',
    payload: data,
    endpoint: apiEndpoints.UPDATE_MERCHANT_TPV,
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const caseManagementAuthApi = (payload) => {
  return apiCall({
    method: 'get',
    payload,
    endpoint: apiEndpoints.CASEMANAGEMENT_AUTH,
  });
};

export const getTicketList = (data:any, authParams:any):void => {
  return apiCall({
    method: 'get',
    payload: data,
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_TICKET_LIST(data),
    cm_access_token: authParams.access_token,
    cm_token_type: authParams.token_type,
  });
};

export const getTicketDetail = (ticketId:string, authParams:any):void => {
  return apiCall({
    method: 'get',
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_TICKET_DETAIL(ticketId),
    cm_access_token: authParams.accessToken,
    cm_token_type: authParams.tokenType,
  });
};

export const getCategoriesList = ( authParams:any):void => {
  return apiCall({
    method: 'get',
    payload: {},
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_ALLCATEGORY,
    cm_access_token: authParams.access_token,
    cm_token_type: authParams.token_type,
  });
};

export const getSubCategoriesList = (data:any, authParams:any):void => {
  return apiCall({
    method: 'get',
    payload: {},
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_SUBCATEGORY(data),
    cm_access_token: authParams.access_token,
    cm_token_type: authParams.token_type,
  });
};

export const createTicket = (data:any, authParams:any):void => {
  return apiCall({
    method: 'post',
    payload: data,
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.NEW_TICKET,
    cm_access_token: authParams.access_token,
    cm_token_type: authParams.token_type,
  });
};

export const uploadDocumentsApi = (data:any, authParams:any):void => {
  return apiCall({
    method: 'post',
    payload: data,
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.UPLOAD_DOCUMENT,
    cm_access_token: authParams.access_token,
    cm_token_type: authParams.token_type,
  });
};

export const linkDocumentsApi = (ticketId: string, data:any, authParams:any):void => {
  return apiCall({
    method: 'post',
    payload: data,
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.LINK_DOCUMENT_TO_TICKET(ticketId),
    cm_access_token: authParams.access_token,
    cm_token_type: authParams.token_type,
  });
};

export const fetchAllCommentsApi = (ticketId: string, authParams:any):void => {
  return apiCall({
    method: 'get',
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_ALL_COMMENTS(ticketId),
    cm_access_token: authParams.accessToken,
    cm_token_type: authParams.tokenType,
  });
};

export const addCommentsApi = (ticketId: string, data:any, authParams:any):void => {
  return apiCall({
    method: 'post',
    payload: data,
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_ALL_COMMENTS(ticketId),
    cm_access_token: authParams.accessToken,
    cm_token_type: authParams.tokenType,
  });
};

export const getAllAttachementsApi = (ticketId: string, authParams:any):void => {
  return apiCall({
    method: 'get',
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_ALL_ATTACHEMENTS(ticketId),
    cm_access_token: authParams.accessToken,
    cm_token_type: authParams.tokenType,
  });
};

export const attachmentPreviewApi = (ticketId: string, documentId: string,  authParams:any):void => {
  return apiCall({
    method: 'get',
    isCaseManagementEndPoint: true,
    endpoint: apiEndpoints.GET_PREVIEW(ticketId, documentId),
    cm_access_token: authParams.accessToken,
    cm_token_type: authParams.tokenType,
    isPreview: true,
  });
};

export const fetchOffersRuleApi = (data: MerchantIdType) => {
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.FETCH_OFFERS_RULE(data),
  });
};

export const setOfferRulesApi = (data: FeatureIdWithMID) => {
  return apiCall({
    method: 'put',
    endpoint: apiEndpoints.FETCH_OFFERS_RULE(data.merchantId),
    payload: {
      ruleId: data.id,
    },
  });
};

export const getActiveEdgeWebhookEventsApi = (payload = {}) => {
  const { cancelKey } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: `${apiEndpoints.GET_ACTIVE_EDGE_WEBHOOK_EVENTS}`,
    cancelKey,
  });
};

export const updateEdgeWebhookEventsApi = (payload) => {
  return apiCall({
    method: 'post',
    payload,
    endpoint: apiEndpoints.GET_ACTIVE_EDGE_WEBHOOK_EVENTS,
  });
};

export const getV3MerchantWebhookApi = (payload: any = {}) => {
  const { cancelKey, requestPayload } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.V3_MERCHANT_WEBHOOK,
    query: {
      merchantId: requestPayload?.merchantId,
    },
    cancelKey,
  });
};

export const updateV3MerchantWebhookApi = (payload) => {
  const { merchantId, webhookUrl } = payload;
  return apiCall({
    method: 'put',
    payload: {
      merchantId,
      webhookUrl,
      isWebhookEnable: true,
    },
    endpoint: apiEndpoints.V3_MERCHANT_WEBHOOK,
  });
};

export const getActiveEdgePaymentLinksWebhookEventsApi = (payload = {}) => {
  const { cancelKey } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.GET_ACTIVE_EDGE_PAYMENT_LINKS_WEBHOOK_EVENTS,
    cancelKey,
  });
};

export const updateEdgePaymentLinksWebhooksApi = (payload) => {
  return apiCall({
    method: 'post',
    payload,
    endpoint: apiEndpoints.UPDATE_PAYMENT_LINKS_WEBHOOK_API,
  });
};

export const updateUnifiedMerchantVersionApi = (payload) => {
  const { cancelKey, ...requestPayload } = payload || {};
  return apiCall({
    method: 'post',
    payload: requestPayload,
    endpoint: apiEndpoints.UPDATE_MERCHANT_VERSION,
    cancelKey,
  });
};

export const getCheckoutVersionApi = (payload = {}) =>{
  const { cancelKey } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.GET_CHECKOUT_VERSION,
    cancelKey,
  });
};


// apple pay api's-----------------------------------------


// download csr in case of app

export const getCsrApplePayApi = (payload) => {
  return apiCall({
    method: 'post',
    endpoint: apiEndpoints.GET_DOWNLOAD_CSR_APPLE_PAY,
    payload
  });
};


// upload api for apple pay certificates


// get wallet status 


export const getApplePayWalletStatusApi = (payload = {}) => {
  const { cancelKey } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.GET_APPLE_PAY_WALLET_STATUS,
    cancelKey,
  });
};


// get apple pay integration status

export const getApplePayIntegrationStatusApi = (payload = {}) => {
  const { cancelKey } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.GET_APPLE_PAY_INTEGRATION_STATUS,
    cancelKey,
  });
};


// web registration api

export const webRegistrationApplePayApi = (payload) => {
  return apiCall({
    method: 'post',
    endpoint: apiEndpoints.WEB_REGISTRATION_APPLE_PAY,
    payload
  });
};


// apple pay activate certificates

export const activateApplePayCertificatesApi = (payload) => {
  return apiCall({
    method: 'post',
    endpoint: apiEndpoints.ACTIVATE_APPLE_PAY_CERTIFICATES,
    payload
  });
};


// payment modes api

export const getPaymentModesApi = (payload) =>{
  const { cancelKey, requestPayload } = stripCancelKey(payload);
  return apiCall({
    method: 'get',
    endpoint: apiEndpoints.GET_PAYMENT_MODES(requestPayload?.emailId),
    cancelKey,
  });
};

// Updated Dashboard Theme api
export const getDashboardThemeApi = (payload: {
  merchantId: MerchantIdType;
}) => {
  const { merchantId } = payload;
  return apiCall({
    method: 'get',
    endpoint: `${apiEndpoints.GET_DASHBOARD_THEME}/${merchantId}`,
  });
};

export const updateDashboardThemeApi = (payload: {
  merchantId: MerchantIdType;
  brandColor: string;
  brandLogo: string | null;
}) => {
  const { merchantId, brandColor, brandLogo } = payload;
  return apiCall({
    method: 'post',
    payload: {
      merchant_id: merchantId,
      brand_color: brandColor,
      brand_logo: brandLogo,
    },
    endpoint: `${apiEndpoints.UPDATE_DASHBOARD_THEME}`,
  });
};
